import React, { useMemo } from 'react';
import { Card, CardHeader } from './primitives';
import { formatShortDateTime } from '../shared/orderFormat';

/*
 * What happened, in order. Previously hidden behind a second tab, which meant a
 * question like "who cancelled this and when" required knowing the tab existed.
 * It sits under the items now, where it costs nothing until you scroll to it.
 *
 * Messages arrive as HTML fragments from the backend; they are stripped rather
 * than injected, so nothing here can render markup from an order record.
 */

const stripTags = (value) =>
  typeof value === 'string' ? value.replace(/<[^>]*>/g, '').trim() : '';

/*
 * Collapses adjacent rows that are identical in message, timestamp and actor.
 *
 * Order placement currently writes one status-history row per line item rather
 * than one per order, so a two-item order logs "Order Placed" twice and a
 * ten-item order logs it ten times. Confirmed by comparing a single-item order
 * (one row) against a two-item one (two rows, same second, same actor).
 *
 * That is a backend bug and should be fixed there — but the rows already
 * written stay wrong for every existing multi-item order, so the screen has to
 * cope with them regardless.
 *
 * Only *adjacent* duplicates are collapsed. The per-item rows are written in
 * one transaction and so always arrive together; comparing the whole list
 * instead would silently swallow a genuine repeat, such as an operator moving
 * an order back and forth between two stages inside the same minute.
 */
const collapseDuplicates = (entries) => {
  const list = Array.isArray(entries) ? entries : [];
  return list.filter((event, index) => {
    if (index === 0) return true;
    const previous = list[index - 1];
    return !(
      event?.created_on === previous?.created_on &&
      event?.created_by === previous?.created_by &&
      stripTags(event?.message) === stripTags(previous?.message)
    );
  });
};

const OrderTimeline = ({ entries }) => {
  const events = useMemo(() => collapseDuplicates(entries), [entries]);

  return (
    <Card>
      <CardHeader
        title="Timeline"
        action={
          events.length > 0 && (
            <span className="text-[11px] text-nysmah-muted">
              {events.length} {events.length === 1 ? 'event' : 'events'}
            </span>
          )
        }
      />
      <div className="px-3.5 py-3">
        {events.length === 0 ? (
          <p className="text-[12.5px] text-nysmah-muted text-center py-6 m-0">
            Nothing recorded for this order yet.
          </p>
        ) : (
          <ol className="m-0 p-0 list-none flex flex-col">
            {events.map((event, index) => {
              const message = stripTags(event.message) || '—';
              const isLast = index === events.length - 1;
              return (
                <li
                  key={event.id ?? `${event.created_on}-${index}`}
                  className="grid grid-cols-[14px_1fr_auto] gap-3 items-start"
                >
                  <span
                    className="flex flex-col items-center h-full pt-1"
                    aria-hidden="true"
                  >
                    <span
                      className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                        index === 0 ? 'bg-nysmah-accent' : 'bg-[#D6DFE2]'
                      }`}
                    />
                    {!isLast && (
                      <span className="w-px flex-1 min-h-[18px] bg-[#E1E7EA] mt-1" />
                    )}
                  </span>
                  <div className={isLast ? 'pb-0' : 'pb-3'}>
                    <p className="text-[12.5px] text-nysmah-dark m-0">
                      {message}
                    </p>
                    {event.created_by && (
                      <p className="text-[11px] text-nysmah-muted m-0 mt-0.5">
                        by {event.created_by}
                      </p>
                    )}
                  </div>
                  <time className="text-[11px] text-nysmah-muted tabular-nums whitespace-nowrap pt-0.5">
                    {event.created_on ? formatShortDateTime(event.created_on) : ''}
                  </time>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </Card>
  );
};

export default OrderTimeline;
